import { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';

import { GhostButton } from 'ui/button';
import { Close16 } from 'ui/icons';
import { LeftTooltip } from 'ui/tooltip';
import { BaseOption, TypeaheadSelect } from 'ui/selects';
import { pxToRem } from 'utils/convert';
import { ListSelect } from 'modules/workflowNodeEditors/components/formElements';
import { LIST_TYPES } from 'constants/listTypes';
import { BaseRow } from './SplitEditor.style';
import { getSplitRemoveTestId, getSplitSelectTestId } from './testUtils';

const fullWidthSelect = css`
  width: 100%;

  > [role*='listbox'] {
    width: 100%;
  }
`;

const FullWidthTypeaheadSelect = styled(TypeaheadSelect)`
  ${fullWidthSelect}
`;

const FullWidthListSelect = styled(ListSelect)`
  ${fullWidthSelect}
`;

const RowSpacer = styled.div`
  display: flex;
  width: ${pxToRem(480)};
`;

type SplitSelectProps = {
  canRemove: boolean;
  hasError?: boolean;
  hasFetchedOptions?: boolean;
  isReadOnly?: boolean;
  itemIndex?: number;
  onRemove: (index: number) => void;
  onSelect: (value: string, index: number) => void;
  options: BaseOption<string>[];
  placeholder?: string;
  splitEditor: string;
  value: string;
  // The field that has been selected for this split editor.
  field?: string;
};

const LIST_FIELD = 'emailListIds';

export const SplitTypeaheadSelect: FunctionComponent<React.PropsWithChildren<SplitSelectProps>> = ({
  canRemove,
  hasError = false,
  hasFetchedOptions = false,
  itemIndex = 0,
  onRemove,
  onSelect,
  options,
  placeholder,
  splitEditor,
  isReadOnly = false,
  value,
  field,
}) => {
  const hasNoValuesForAttribute = hasFetchedOptions && options?.length === 0;
  const shouldShowReadOnlyTooltip = isReadOnly && !!value;
  const tooltipContent = shouldShowReadOnlyTooltip
    ? value
    : 'This project does not have values for this attribute.';

  return (
    <BaseRow>
      <LeftTooltip
        isEnabled={hasNoValuesForAttribute || shouldShowReadOnlyTooltip}
        tooltipContent={tooltipContent}
      >
        <RowSpacer>
          {field === LIST_FIELD ? (
            <FullWidthListSelect
              onChange={(val: string) => onSelect(val as string, itemIndex)}
              value={value}
              placeholder={placeholder}
              isError={hasError}
              isDisabled={isReadOnly}
              listType={[LIST_TYPES.STANDARD, LIST_TYPES.INTERNAL]}
              isFullWidth
              isMulti={false}
              data-test={getSplitSelectTestId('list', itemIndex)}
            />
          ) : (
            <FullWidthTypeaheadSelect
              defaultValues={[value]}
              id={`split-select-${itemIndex}`}
              onSelect={([val]) => onSelect(val as string, itemIndex)}
              options={options}
              placeholder={placeholder}
              data-test={getSplitSelectTestId(splitEditor, itemIndex)}
              isError={hasError}
              isDisabled={isReadOnly}
              isFullWidth
            />
          )}
          {canRemove && !isReadOnly && (
            <GhostButton
              onClick={() => onRemove(itemIndex)}
              data-test={getSplitRemoveTestId(splitEditor, itemIndex)}
            >
              <Close16 />
            </GhostButton>
          )}
        </RowSpacer>
      </LeftTooltip>
    </BaseRow>
  );
};
